import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Check, Lock, Download, RefreshCw, CreditCard, Clock } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { CountdownTimer } from './CountdownTimer';
export function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const features = ['Pre-K and Kindergarten Module', 'Elementary Module — Grades 1-6', 'Middle and High School — Grades 7-12', 'Bank of 100+ Musical Activities', 'All files in Word and PowerPoint', '100% editable — make it your own', 'Works for any school or curriculum', 'Instant download after payment', 'LIFETIME ACCESS — pay once, keep everything forever', 'All future updates included free'];
  return <section id="pricing" ref={ref} className="w-full bg-cream py-12 md:py-16 px-4" data-id="element-243">
      <div className="max-w-4xl mx-auto" data-id="element-244">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 md:mb-12" data-id="element-245">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3 md:mb-4 px-2" data-id="element-246">
            Get Everything For a Single One-Time Payment
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-charcoal max-w-3xl mx-auto px-2" data-id="element-247">
            No subscription. No renewals. Yours forever.
          </p>
          <p className="text-sm sm:text-base md:text-lg text-charcoal mt-2 md:mt-3 max-w-2xl mx-auto px-2" data-id="element-248">
            For less than the cost of a Friday night dinner, you get a complete
            year of music lessons ready to use starting tomorrow morning.
          </p>
        </motion.div>

        {/* Pricing Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-white rounded-2xl shadow-2xl border-4 border-gold p-4 sm:p-6 md:p-10" data-id="element-249">
          <div className="text-center mb-6 md:mb-8" data-id="element-250">
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-2" data-id="element-251">
              MEGA MUSIC PACK
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-charcoal" data-id="element-252">
              Complete Teacher Resource Bundle
            </p>
          </div>

          <div className="text-center mb-6 md:mb-8 pb-6 md:pb-8 border-b-2 border-cream" data-id="element-253">
            <div className="mb-2 md:mb-4" data-id="element-254">
              <span className="text-lg sm:text-xl md:text-2xl text-charcoal line-through opacity-60" data-id="element-255">
                $47 USD
              </span>
            </div>
            <div className="mb-2" data-id="element-256">
              <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-navy" data-id="element-257">
                $17 USD
              </span>
            </div>
            <div className="mb-3 md:mb-4" data-id="element-258">
              <span className="text-base sm:text-lg md:text-xl font-semibold text-charcoal" data-id="element-259">
                ONE-TIME PAYMENT — SAVE 63%
              </span>
            </div>
          </div>

          <div className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-260">
            {features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.4,
            delay: index * 0.03
          }} className="flex items-start gap-2 md:gap-3" data-id="element-261">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-262" />
                <span className="text-sm sm:text-base md:text-lg text-charcoal leading-relaxed" data-id="element-263">
                  {feature}
                </span>
              </motion.div>)}
          </div>

          <div className="flex justify-center mb-6 md:mb-8" data-id="element-264">
            <CtaButton className="text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-6 w-full sm:w-auto" data-id="element-265">
              <span className="hidden sm:inline" data-id="element-266">
                YES, I WANT INSTANT ACCESS — $17 USD
              </span>
              <span className="sm:hidden" data-id="element-267">GET ACCESS — $17</span>
            </CtaButton>
          </div>

          {/* Payment Logos */}
          <div className="border-t-2 border-cream pt-4 sm:pt-6" data-id="element-268">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm md:text-base" data-id="element-269">
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-navy opacity-60" data-id="element-270" />
              <span className="text-charcoal font-medium" data-id="element-271">Visa</span>
              <span className="text-charcoal font-medium" data-id="element-272">Mastercard</span>
              <span className="text-charcoal font-medium" data-id="element-273">Amex</span>
              <span className="text-charcoal font-medium" data-id="element-274">PayPal</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm md:text-base text-charcoal" data-id="element-275">
              <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-navy flex-shrink-0" data-id="element-276" />
              <span className="font-semibold text-center" data-id="element-277">
                100% Secure and Encrypted Checkout
              </span>
            </div>
          </div>
        </motion.div>

        {/* Trust Copy Below Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-8 md:mt-12 text-center max-w-3xl mx-auto" data-id="element-278">
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-2 border-gold" data-id="element-279">
            <p className="text-base sm:text-lg md:text-xl font-bold text-navy mb-3 sm:mb-4" data-id="element-280">
              This is a one-time payment of $17.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-charcoal leading-relaxed" data-id="element-281">
              You will never be charged again. No monthly fee. No annual
              renewal. No surprise charges of any kind.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-charcoal leading-relaxed mt-3 sm:mt-4" data-id="element-282">
              You get instant access to every file the moment your payment goes
              through. Download everything. Use it forever.
            </p>
            <p className="text-base sm:text-lg md:text-xl font-semibold text-navy mt-3 sm:mt-4" data-id="element-283">
              That is the deal.
            </p>
          </div>
        </motion.div>

        {/* Urgency Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="mt-8 md:mt-12 text-center" data-id="element-284">
          <div className="bg-navy text-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl" data-id="element-285">
            <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4" data-id="element-286">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0" data-id="element-287" />
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold" data-id="element-288">
                This Price Will Not Last
              </h3>
            </div>
            <p className="text-xs sm:text-sm md:text-base mb-2" data-id="element-289">
              The regular price of the Mega Music Pack is{' '}
              <span className="font-bold" data-id="element-290">$47 USD</span>.
            </p>
            <p className="text-xs sm:text-sm md:text-base mb-4 sm:mb-6" data-id="element-291">
              The $17 launch price is available for a limited time only.
            </p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-gold mb-4 sm:mb-6" data-id="element-292">
              Once this offer closes, it closes for good.
            </p>

            {/* Countdown Timer */}
            <div className="my-6 sm:my-8" data-id="element-293">
              <p className="text-xs sm:text-sm md:text-base mb-3 sm:mb-4 text-gold-light" data-id="element-294">
                This offer expires when the timer hits zero:
              </p>
              <CountdownTimer data-id="element-295" />
            </div>

            {/* CTA Below Timer */}
            <div className="mt-6 sm:mt-8" data-id="element-296">
              <CtaButton className="bg-gold hover:bg-gold-light text-navy font-bold text-base sm:text-lg md:text-xl px-6 sm:px-10 md:px-12 py-4 sm:py-5 md:py-6 w-full sm:w-auto" data-id="element-297">
                <span className="hidden sm:inline" data-id="element-298">
                  GET LIFETIME ACCESS FOR $17 — RIGHT NOW
                </span>
                <span className="sm:hidden" data-id="element-299">GET ACCESS NOW — $17</span>
              </CtaButton>
              <p className="text-xs sm:text-sm md:text-base mt-3 sm:mt-4 text-white opacity-90" data-id="element-300">
                One payment. Instant access. Yours forever.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Micro Guarantee Reminder */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-8 md:mt-12 text-center max-w-3xl mx-auto" data-id="element-301">
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-2 border-navy" data-id="element-302">
            <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-navy mb-3 sm:mb-4" data-id="element-303">
              Not sure yet?
            </h4>
            <p className="text-sm sm:text-base md:text-lg text-charcoal leading-relaxed mb-3 sm:mb-4" data-id="element-304">
              You have <span className="font-bold text-navy" data-id="element-305">7 full days</span>{' '}
              to try every single file in this pack with your real students in
              your real classroom.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-charcoal leading-relaxed mb-3 sm:mb-4" data-id="element-306">
              If it is not everything it promises to be, email us and we send
              your $17 back. Every cent. No questions asked.
            </p>
            <p className="text-base sm:text-lg md:text-xl font-bold text-navy" data-id="element-307">
              You are risking nothing.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}